import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Tooltip,
  Slider,
  RadioGroup,
  FormControl,
  FormLabel,
  Radio,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist';
import { useHelpContent } from '../../utils/HelpUtils';
import { contentTextLooksRtl } from '../../utils/contentRichPreviewHtml';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export type SuggestTreeImportMode = 'category_tree' | 'content_only';

export type ImportFilePage = {
  page: number;
  text: string;
};

export type ExtractedImportFile = {
  fileName: string;
  isPaged: boolean;
  pages: ImportFilePage[];
  sourceFile?: File;
};

type PagePreviewTarget = {
  fileName: string;
  page: number;
  text: string;
  sourceFile?: File;
};

const ImportPagePreviewDialog: React.FC<{
  preview: PagePreviewTarget | null;
  onClose: () => void;
}> = ({ preview, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rendering, setRendering] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);

  const isPdf = Boolean(preview?.sourceFile && /\.pdf$/i.test(preview.fileName));
  const previewText = preview?.text?.trim() || '(empty page)';
  const previewIsRtl = contentTextLooksRtl(preview?.text ?? '');

  useEffect(() => {
    if (!preview || !isPdf || !preview.sourceFile) {
      setRendering(false);
      setShowCanvas(false);
      return;
    }
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | null = null;
    let pdf: { destroy: () => Promise<void> } | null = null;
    setRendering(true);
    setShowCanvas(false);
    (async () => {
      try {
        const data = await preview.sourceFile!.arrayBuffer();
        if (cancelled) return;
        const loaded = await pdfjsLib.getDocument({ data }).promise;
        pdf = loaded;
        const page = await loaded.getPage(preview.page);
        if (cancelled) return;
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        if (!canvas || !context) {
          if (!cancelled) setRendering(false);
          return;
        }
        const viewport = page.getViewport({ scale: 1.2 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        renderTask = page.render({
          canvas,
          canvasContext: context,
          viewport,
        } as Parameters<typeof page.render>[0]);
        await renderTask.promise;
        if (!cancelled) {
          setShowCanvas(true);
          setRendering(false);
        }
      } catch {
        if (!cancelled) {
          setShowCanvas(false);
          setRendering(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      renderTask?.cancel();
      void pdf?.destroy();
    };
  }, [preview, isPdf]);

  return (
    <Dialog open={Boolean(preview)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {preview ? `${preview.fileName} — Page ${preview.page}` : 'Page preview'}
      </DialogTitle>
      <DialogContent>
        {isPdf && (
          <Box
            sx={{
              mb: 2,
              textAlign: 'center',
              bgcolor: 'action.hover',
              p: 1,
              overflow: 'auto',
              maxHeight: '70vh',
            }}
          >
            {rendering && <CircularProgress size={28} sx={{ my: 2 }} />}
            <canvas
              ref={canvasRef}
              style={{
                display: showCanvas ? 'inline-block' : 'none',
                maxWidth: '100%',
                height: 'auto',
              }}
            />
            {!rendering && !showCanvas && (
              <Typography variant="body2" color="text.secondary">
                Could not render this page. Text is shown below.
              </Typography>
            )}
          </Box>
        )}
        {(!isPdf || !showCanvas) && (
          <Typography
            variant="body2"
            component="pre"
            dir={previewIsRtl ? 'rtl' : 'ltr'}
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              m: 0,
              textAlign: previewIsRtl ? 'right' : 'left',
            }}
          >
            {previewText}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export type ImportScopeChange = {
  filteredText: string;
  canProceed: boolean;
};

function pageKey(fileIndex: number, page: number): string {
  return `${fileIndex}:${page}`;
}

export function buildFilteredImportText(
  files: ExtractedImportFile[],
  selectedPageKeys: Set<string>,
): string {
  const parts: string[] = [];
  files.forEach((file, fileIndex) => {
    const pageTexts = file.pages
      .filter((p) => selectedPageKeys.has(pageKey(fileIndex, p.page)))
      .map((p) => p.text.trim())
      .filter(Boolean);
    if (pageTexts.length === 0) return;
    const body = pageTexts.join('\n\n');
    parts.push(files.length > 1 ? `=== FILE: ${file.fileName} ===\n${body}` : body);
  });
  return parts.join('\n\n');
}

export const ImportScopePicker: React.FC<{
  files: ExtractedImportFile[];
  resetToken: boolean;
  onScopeChange: (scope: ImportScopeChange) => void;
}> = ({ files, resetToken, onScopeChange }) => {
  const { getTooltipTextForState } = useHelpContent();
  const [selectedPageKeys, setSelectedPageKeys] = useState<Set<string>>(new Set());
  const [pagePreview, setPagePreview] = useState<PagePreviewTarget | null>(null);
  const onScopeChangeRef = useRef(onScopeChange);
  onScopeChangeRef.current = onScopeChange;

  const allPageKeys = useMemo(() => {
    const keys: string[] = [];
    files.forEach((file, fileIndex) => {
      file.pages.forEach((p) => keys.push(pageKey(fileIndex, p.page)));
    });
    return keys;
  }, [files]);

  const showPages = files.some((f) => f.isPaged && f.pages.length > 1);

  useEffect(() => {
    setSelectedPageKeys(new Set(allPageKeys));
  }, [resetToken, allPageKeys]);

  useEffect(() => {
    const filteredText = buildFilteredImportText(files, selectedPageKeys);
    onScopeChangeRef.current({ filteredText, canProceed: selectedPageKeys.size > 0 });
  }, [files, selectedPageKeys]);

  if (!showPages) return null;

  const toggleKey = (set: Set<string>, key: string, next: boolean) => {
    const copy = new Set(set);
    if (next) copy.add(key);
    else copy.delete(key);
    return copy;
  };

  return (
    <Box sx={{ mb: 2 }}>
      {showPages && (
        <Box>
          <Tooltip title={getTooltipTextForState('import-file-pages', true)} placement="top">
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Pages to include
            </Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Uncheck pages that belong to interviews you do not want to import. Use Preview to see
            the page.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            <Button size="small" onClick={() => setSelectedPageKeys(new Set(allPageKeys))}>
              All pages
            </Button>
            <Button size="small" onClick={() => setSelectedPageKeys(new Set())}>
              None
            </Button>
          </Box>
          <Box
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              px: 1,
            }}
          >
            {files.map((file, fileIndex) => (
              <Box key={`${file.fileName}-${fileIndex}`} sx={{ py: 0.5 }}>
                {files.length > 1 && file.isPaged && (
                  <Typography variant="caption" color="text.secondary">
                    {file.fileName}
                  </Typography>
                )}
                {file.isPaged &&
                  file.pages.map((p) => {
                    const key = pageKey(fileIndex, p.page);
                    const preview = p.text.replace(/\s+/g, ' ').trim().slice(0, 80);
                    return (
                      <Box
                        key={key}
                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}
                      >
                        <FormControlLabel
                          sx={{ display: 'flex', alignItems: 'flex-start', ml: 0, flex: 1 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedPageKeys.has(key)}
                              onChange={(e) =>
                                setSelectedPageKeys((prev) =>
                                  toggleKey(prev, key, e.target.checked),
                                )
                              }
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Page {p.page}
                              {preview ? ` — ${preview}${p.text.trim().length > 80 ? '…' : ''}` : ''}
                            </Typography>
                          }
                        />
                        <Button
                          size="small"
                          onClick={() =>
                            setPagePreview({
                              fileName: file.fileName,
                              page: p.page,
                              text: p.text,
                              sourceFile: file.sourceFile,
                            })
                          }
                        >
                          Preview
                        </Button>
                      </Box>
                    );
                  })}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <ImportPagePreviewDialog preview={pagePreview} onClose={() => setPagePreview(null)} />
    </Box>
  );
};

interface SuggestTreeContextDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed: (
    context: string,
    maxDepth: number,
    treeStrategy: 'top_down' | 'bottom_up',
    importMode: SuggestTreeImportMode,
    charsPerSegment: number,
    filteredText: string,
  ) => void;
  fileNames: string[];
  extractedFiles?: ExtractedImportFile[];
}

const DEFAULT_CHARS_PER_SEGMENT = 600;
const MIN_CHARS_PER_SEGMENT = 100;
const MAX_CHARS_PER_SEGMENT = 20000;

const SuggestTreeContextDialog: React.FC<SuggestTreeContextDialogProps> = ({
  open,
  onClose,
  onProceed,
  fileNames,
  extractedFiles = [],
}) => {
  const [context, setContext] = useState('');
  const [maxDepth, setMaxDepth] = useState(1);
  const [treeStrategy, setTreeStrategy] = useState<'top_down' | 'bottom_up'>('bottom_up');
  const [importMode, setImportMode] = useState<SuggestTreeImportMode>('category_tree');
  const [charsPerSegment, setCharsPerSegment] = useState<number>(DEFAULT_CHARS_PER_SEGMENT);
  const [scope, setScope] = useState<ImportScopeChange>({ filteredText: '', canProceed: true });
  const { getTooltipTextForState } = useHelpContent();

  const clampChars = (value: number) =>
    Math.max(MIN_CHARS_PER_SEGMENT, Math.min(MAX_CHARS_PER_SEGMENT, value));

  const handleProceed = () => {
    onProceed(
      context.trim(),
      maxDepth,
      treeStrategy,
      importMode,
      clampChars(charsPerSegment),
      scope.filteredText,
    );
  };

  const handleClose = () => {
    setContext('');
    setMaxDepth(1);
    setTreeStrategy('bottom_up');
    setImportMode('category_tree');
    setCharsPerSegment(DEFAULT_CHARS_PER_SEGMENT);
    onClose();
  };

  const isProceedDisabled = () => {
    return !scope.canProceed;
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>New document from files</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected files
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {fileNames.map((fileName, index) => (
              <Chip key={index} label={fileName} variant="outlined" size="small" />
            ))}
          </Box>
        </Box>

        <ImportScopePicker
          files={extractedFiles}
          resetToken={open}
          onScopeChange={setScope}
        />

        <Box sx={{ mb: 2 }}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">
              <Tooltip title={getTooltipTextForState('suggest-tree-import-mode', true)} placement="top">
                <span>Import mode</span>
              </Tooltip>
            </FormLabel>
            <RadioGroup
              value={importMode}
              onChange={(e) => setImportMode(e.target.value as SuggestTreeImportMode)}
            >
              <FormControlLabel
                value="category_tree"
                control={<Radio />}
                label="Build category structure"
              />
              <FormControlLabel
                value="content_only"
                control={<Radio />}
                label="Content only (no folders)"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Tooltip title={getTooltipTextForState('suggest-tree-chars-per-section', true)} placement="top">
            <TextField
              type="number"
              label="Characters per section"
              value={charsPerSegment}
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                setCharsPerSegment(Number.isNaN(parsed) ? DEFAULT_CHARS_PER_SEGMENT : parsed);
              }}
              onBlur={() => setCharsPerSegment((prev) => clampChars(prev))}
              inputProps={{ min: MIN_CHARS_PER_SEGMENT, max: MAX_CHARS_PER_SEGMENT, step: 100 }}
              variant="outlined"
              size="small"
              helperText={`Target size before starting a new section (default ${DEFAULT_CHARS_PER_SEGMENT})`}
              sx={{ width: 260 }}
            />
          </Tooltip>
        </Box>

        <Tooltip title={getTooltipTextForState('suggest-tree-context', true)} placement="top">
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Context (optional)"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. legal contracts; organize by chapter"
            variant="outlined"
            sx={{ mb: 2 }}
          />
        </Tooltip>

        {importMode === 'category_tree' && (
          <Box sx={{ mb: 2 }}>
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">
                <Tooltip title={getTooltipTextForState('suggest-tree-strategy', true)} placement="top">
                  <span>Building strategy</span>
                </Tooltip>
              </FormLabel>
              <RadioGroup
                row
                value={treeStrategy}
                onChange={(e) => setTreeStrategy(e.target.value as 'top_down' | 'bottom_up')}
              >
                <FormControlLabel
                  value="top_down"
                  control={<Radio />}
                  label="Top-down (broad first)"
                />
                <FormControlLabel
                  value="bottom_up"
                  control={<Radio />}
                  label="Bottom-up (fine then group, default)"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {importMode === 'category_tree' && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Maximum tree depth: {maxDepth}
            </Typography>
            <Tooltip title={getTooltipTextForState('suggest-tree-max-depth', true)} placement="top">
              <Slider
                value={maxDepth}
                onChange={(_, value) => setMaxDepth(value as number)}
                min={1}
                max={6}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
            </Tooltip>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
          <Button onClick={handleClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getTooltipTextForState('suggest-tree-proceed', true)} placement="top">
          <Button 
            onClick={handleProceed} 
            variant="contained" 
            disabled={isProceedDisabled()}
          >
            {importMode === 'content_only' ? 'Prepare content' : 'Generate preview'}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SuggestTreeContextDialog; 